import { Order } from '~api/tos';

import { useState } from 'react';

import {
  Button,
  ListLayout,
  Modal,
  ModalSelect,
  OptionList,
  PileBadge,
  SearchInput,
  Spinner,
} from '@ingrid/luterkort-react';

import { SelectModal, SelectModalProps } from '~src/components/Modal/Modal';
import { LocalizedDate } from '~src/i18n/localized-date';
import { useTranslation } from '~src/i18n/use-translation';
import { useTransportOrdersListQuery } from '~src/pages/order/queries';

export type ReferencesSelectTosProps = {
  mode: 'add' | 'update';
  onValueChange: (order?: Order) => void;
  value?: Order;
} & Pick<SelectModalProps, 'trigger'>;

export function ReferencesSelectTos({ mode, onValueChange, value, ...modalProps }: ReferencesSelectTosProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState<string | undefined>();
  const { data, isLoading } = useTransportOrdersListQuery(search ? { 'filtering.id': search } : { pageSize: 3 });

  return (
    <SelectModal
      filterFactory={false}
      searchValue={search}
      onSearchValueChange={setSearch}
      value={value?.id ? [value.id] : ['']}
      onValueChange={val => onValueChange(data?.orders?.find(o => o.id === val[0]))}
      {...modalProps}
    >
      <Modal.Header title={t(mode === 'add' ? 'shipments.references.addTosId' : 'shipments.references.editTosId')} />

      <ModalSelect.Content>
        <ModalSelect.Control>
          <SearchInput placeholder={t('shipments.references.searchTosIdPlaceholder')} />
        </ModalSelect.Control>

        {isLoading && <Spinner />}

        {data?.orders && (
          <OptionList.Viewport>
            {data.orders.map(o => (
              <OptionList.Item key={o.id} spacing="sm" value={o.id!}>
                <ListLayout.Row>
                  <ListLayout.Stack>
                    <ListLayout.SubRow>
                      <ListLayout.PrimaryText>
                        <OptionList.ItemText>{o.id}</OptionList.ItemText>
                      </ListLayout.PrimaryText>
                      <PileBadge>{o.metadata?.source}</PileBadge>
                    </ListLayout.SubRow>
                    <ListLayout.SecondaryText
                      css={{
                        lineHeight: '20px',
                      }}
                    >
                      {t('shipments.references.fromSenderToReceiver', {
                        sender: o.sender?.contact_info?.address?.name,
                        receiver: o.receiver?.contact_info?.address?.name,
                      })}
                      <br />

                      {o.created_at && (
                        <>
                          {t('common.createdAt')} <LocalizedDate dateTime={o.created_at} inline />
                        </>
                      )}
                    </ListLayout.SecondaryText>
                  </ListLayout.Stack>
                  <OptionList.ItemIndicator>
                    <OptionList.CheckmarkIcon />
                  </OptionList.ItemIndicator>
                </ListLayout.Row>
              </OptionList.Item>
            ))}
          </OptionList.Viewport>
        )}
      </ModalSelect.Content>
      <Modal.Footer>
        <Modal.CloseButton>
          <Button variant="secondary">{t('common.cancel')}</Button>
        </Modal.CloseButton>
        <Button>{t(mode === 'add' ? 'common.addReference' : 'common.updateReference')}</Button>
      </Modal.Footer>
    </SelectModal>
  );
}
